import type { MetricCanonicalName, MetricInfo, SetPlanInfo } from '@az3fr/core';
import { memo, useMemo } from 'react';

import type { SelectOptionConfig } from '@/lib/option-generators';
import { VirtualSelect } from '@/components/ui/virtual-select';
import {
	formatDistanceNumber,
	formatDuration,
	generateDistanceOptions,
	generateHeightOptions,
	generateMetricDurationOptions,
	generateRepPaceOptions,
	generateRepetitionOptions,
	generateWeightOptions,
} from '@/lib/option-generators';
import { snakeToCamel } from '@/lib/utils';

interface MetricInputProps {
	metric: MetricInfo;
	setPlan: SetPlanInfo | null;
	onValueChange: (value: SelectOptionConfig<number> | null) => void;
	buttonClassName?: string;

	// Optional override for custom options
	options?: SelectOptionConfig[];

	// UI customization
	disabled?: boolean;
}

function MetricInput({
	metric,
	setPlan,
	onValueChange,
	options: customOptions,
	buttonClassName,
	disabled,
}: MetricInputProps) {
	const { value } = useMetricValue(metric, setPlan);
	const options = useMetricOptions(metric.canonicalName, customOptions);

	const displayValue = formatDisplayValue(value, metric.canonicalName);
	const selectedOption = useMemo(
		() => options.find((option) => option.value === value) ?? null,
		[options, value],
	);

	console.log('options', options);
	console.log('value', value);
	console.log('selectedOption', selectedOption);

	return (
		<VirtualSelect
			value={selectedOption}
			displayValue={displayValue}
			placeholder="-"
			options={options}
			onChange={onValueChange}
			enableVirtualization={true}
			disabled={disabled}
			itemHeight={36}
			maxVisibleItems={8}
			buttonClassName={buttonClassName}
		/>
	);
}

// Custom Hooks
function useMetricValue(metric: MetricInfo, setTemplate: SetPlanInfo | null) {
	return useMemo(() => {
		const metricKey = snakeToCamel(metric.targetColumn);
		const rawValue = setTemplate?.[metricKey as keyof SetPlanInfo];
		const value: number | null = typeof rawValue === 'number' ? rawValue : null;
		const unit = metric.canonicalName === 'reps' ? '' : (metric.unitAbbreviation ?? metric.unit);

		return { value, unit };
	}, [metric, setTemplate]);
}

function useMetricOptions(
	metricCanonicalName: MetricCanonicalName,
	customOptions?: SelectOptionConfig[],
) {
	return useMemo(() => {
		// 1. If custom options provided, use them
		// biome-ignore lint/nursery/noUnnecessaryConditions: i don't know why biome is complaining about this
		if (customOptions) {
			return customOptions;
		}

		// 2. Generate options using special metric logic or shared utilities
		const options = generateOptionsFromRangeLocal(metricCanonicalName);

		return options;
	}, [metricCanonicalName, customOptions]);
}

// Utility Functions - Updated to use shared utilities
function generateOptionsFromRangeLocal(metricType: MetricCanonicalName): SelectOptionConfig[] {
	// Special handling for metrics with custom stepped ranges
	switch (metricType) {
		case 'reps':
			return generateRepetitionOptions();
		case 'duration':
			return generateMetricDurationOptions();
		case 'rep-pace':
			return generateRepPaceOptions();
		case 'distance':
			return generateDistanceOptions();
		case 'height':
			return generateHeightOptions();
		case 'weight':
			return generateWeightOptions();

		default: {
			const _exhaustiveCheck: never = metricType;
			return _exhaustiveCheck;
		}
	}

	// const options: SelectOptionConfig[] = [];

	// for (let i = config.min; i <= config.max; i += config.step) {
	//   const value = Number(i.toFixed(config.precision));

	//   // We'll never have a null label, because the value is never null
	//   const label = formatOptionLabel(value, metricType, config) || '';
	//   options.push({ value, label });
	// }

	// return options;
}

// function formatOptionLabel( value: number | null, metricType: MetricCanonicalName): string | null {
//   if (value === null) {
//     return null;
//   }

//   if (metricType === 'duration') {
//     return formatDuration(value);
//   }

//   return config.precision > 0 ? value.toFixed(config.precision) : value.toString();
// }

function formatDisplayValue(value: number | null, metricType: MetricCanonicalName): string | null {
	if (value === null) {
		return null;
	}

	switch (metricType) {
		case 'distance':
			return formatDistanceNumber(value);
		case 'duration':
			return formatDuration(value);
		case 'rep-pace':
			return formatDuration(value);
		case 'height':
			return value.toString();
		case 'weight':
			return value.toString();
		case 'reps':
			return value.toString();
		default: {
			const _exhaustiveCheck: never = metricType;
			return _exhaustiveCheck;
		}
	}
}

// Memoize the component to prevent unnecessary rerenders
const MemoizedMetricInput = memo(MetricInput);

export { MemoizedMetricInput as MetricInput };
